import {
  QBankStructureStatus,
  QBankStructureType,
  useGetQBankStructureByIdQuery,
  useGetQBankStructureLevelItemsByQBankStructureIdQuery,
  useGetQBankStructureLevelsByQBankStructureIdQuery,
} from '@exam-management/graphql';
import { Navigate } from '@exam-management/router';
import { Loading } from '@shared/components';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { formatQBankStructureLevelItems } from '../utils';

import type {
  FormMethods,
  QBankStructureContextData,
  QBankStructureInformation,
  QBankStructureLevelItemWithLevel,
  QBankStructureProviderProps,
  ValidatePublishQBankStructureError,
} from './types';

const DEFAULT_QBANK_STRUCTURE_INFORMATION = {
  id: '',
  name: '',
  status: QBankStructureStatus.Draft,
  code: 0,
  type: QBankStructureType.Mock,
  description: null,
  remark: null,
} as QBankStructureInformation;

const QBankStructureContext = createContext<QBankStructureContextData | undefined>(undefined);

function QBankStructureProvider(props: Readonly<QBankStructureProviderProps>) {
  const { qBankStructureId, children } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState<ValidatePublishQBankStructureError[]>(
    [],
  );
  const [missingLastItemErrors, setMissingLastItemErrors] = useState<
    ValidatePublishQBankStructureError[]
  >([]);
  const [qBankStructureLevelItems, setQBankStructureLevelItems] = useState<
    QBankStructureLevelItemWithLevel[]
  >([]);
  const formMethodsRef = useRef<FormMethods>(null);

  const {
    data: getQBankStructureResult,
    loading: isGetQBankStructureLoading,
    error: getQBankStructureError,
  } = useGetQBankStructureByIdQuery({
    variables: {
      id: qBankStructureId,
    },
    fetchPolicy: 'cache-first',
  });

  const { data: getQBankStructureLevelsResult, loading: isGetQBankStructureLevelsLoading } =
    useGetQBankStructureLevelsByQBankStructureIdQuery({
      variables: {
        qBankStructureId,
      },
      fetchPolicy: 'cache-first',
    });

  const { data: getQBankStructureLevelItemsResult, loading: isGetQBankStructureLevelItemsLoading } =
    useGetQBankStructureLevelItemsByQBankStructureIdQuery({
      variables: {
        qBankStructureId,
        options: null,
      },
      fetchPolicy: 'cache-first',
    });

  const qBankStructureInformation = useMemo(
    () => getQBankStructureResult?.qbsGetQBankStructureById ?? DEFAULT_QBANK_STRUCTURE_INFORMATION,
    [getQBankStructureResult],
  );

  const qBankStructureLevels = useMemo(
    () =>
      [...(getQBankStructureLevelsResult?.qbsGetQBankStructureLevelsByQBankStructureId ?? [])].sort(
        (a, b) => {
          return a.level - b.level;
        },
      ),
    [getQBankStructureLevelsResult],
  );

  const qBankStructureLevelItemsData = useMemo(
    () => getQBankStructureLevelItemsResult?.qbsGetQBankStructureLevelItemsByQBankStructureId ?? [],
    [getQBankStructureLevelItemsResult],
  );

  const isFetching =
    isGetQBankStructureLoading ||
    isGetQBankStructureLevelsLoading ||
    isGetQBankStructureLevelItemsLoading;

  const value: QBankStructureContextData = useMemo(() => {
    return {
      qBankStructureInformation,
      qBankStructureLevels,
      qBankStructureLevelItems,
      formMethodsRef,
      validatePublishQBankStructure: {
        errors: validationErrors,
        missingLastItemErrors,
        setErrors: (errors: ValidatePublishQBankStructureError[]) => {
          setValidationErrors(errors);
        },
        clearErrors: () => {
          setValidationErrors([]);
          setMissingLastItemErrors([]);
        },
        setMissingLastItemErrors: (errors: ValidatePublishQBankStructureError[]) => {
          setMissingLastItemErrors(errors);
        },
      },
      pageAccessibility: {
        dashboard: true,
        structure: true,
        questionList: [
          QBankStructureStatus.Published,
          QBankStructureStatus.Unpublished,
          QBankStructureStatus.Archived,
        ].includes(qBankStructureInformation.status),
        settings: QBankStructureStatus.Draft !== qBankStructureInformation.status,
        los: true,
        auditLog: true,
      },
    };
  }, [
    qBankStructureInformation,
    qBankStructureLevels,
    qBankStructureLevelItems,
    formMethodsRef,
    validationErrors,
    missingLastItemErrors,
  ]);

  useEffect(() => {
    if (!isFetching) {
      const qBankStructureLevelItemsWithLevel = formatQBankStructureLevelItems(
        qBankStructureLevelItemsData,
        qBankStructureLevels,
      );
      setQBankStructureLevelItems(qBankStructureLevelItemsWithLevel);
      setIsLoading(false);
    }
  }, [isFetching, getQBankStructureLevelItemsResult]);

  if (isLoading || isFetching) {
    return <Loading height='100%' />;
  }

  if (getQBankStructureError) {
    return <Navigate to='/access-denied' replace />;
  }

  return (
    <QBankStructureContext.Provider value={value}>
      {children ?? <Outlet />}
    </QBankStructureContext.Provider>
  );
}

export { QBankStructureContext, QBankStructureProvider };
